import { createClient } from '@supabase/supabase-js';
import { FormData } from '../App';

// Initialize Supabase client
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Enhanced environment variable validation
function validateEnvironment() {
  const issues = [];
  
  if (!supabaseUrl) {
    issues.push('VITE_SUPABASE_URL is not set');
  } else if (!supabaseUrl.startsWith('https://')) {
    issues.push('VITE_SUPABASE_URL appears to be invalid (should start with https://)');
  }
  
  if (!supabaseKey) {
    issues.push('VITE_SUPABASE_ANON_KEY is not set');
  } else if (!supabaseKey.includes('.')) {
    issues.push('VITE_SUPABASE_ANON_KEY appears to be invalid');
  }
  
  if (issues.length > 0) {
    throw new Error(`Environment validation failed:\n${issues.join('\n')}`);
  }
}

// Validate environment before creating client
validateEnvironment();

export const supabase = createClient(supabaseUrl!, supabaseKey!, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true
  },
  global: {
    headers: {
      'x-application-name': 'weed-management-tool'
    }
  },
  db: {
    schema: 'public'
  }
});

export interface Submission extends FormData {
  id: string;
  created_at: string;
  total_cost: number;
  total_duration: number;
  deleted?: boolean;
  deleted_at?: string;
}

export async function initDb() {
  let retries = 3;
  let lastError: Error | null = null;

  while (retries > 0) {
    try {
      // Test the connection with a timeout
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Connection timeout')), 5000);
      });

      const connectionPromise = supabase.from('submissions').select('id').limit(1);
      
      const { error } = await Promise.race([connectionPromise, timeoutPromise]);
      
      if (error) {
        throw error;
      }
      
      console.log('Database connection established successfully');
      return true;
    } catch (error) {
      lastError = error as Error;
      console.warn(`Database connection attempt failed (${retries} retries left):`, error);
      retries--;
      
      if (retries > 0) {
        // Wait for 1 second before retrying
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
  }

  // If we've exhausted all retries, throw the last error
  throw new Error(`Database initialization failed after multiple attempts: ${lastError?.message}`);
}

export async function saveSubmission(data: FormData, totals: { totalCost: number; totalControlDays: number }) {
  try {
    const submissionData = {
      state: data.state,
      district: data.district,
      cropping_pattern: data.croppingPattern,
      herbicide_applications: data.herbicideApplications,
      manual_weeding: data.manualWeeding,
      herbicides: data.herbicides,
      manual_weeding_details: data.manualWeedingDetails,
      crop_safety_rating: data.cropSafetyRating,
      herbicide_efficacy_rating: data.herbicideEfficacyRating,
      total_cost: totals.totalCost,
      total_duration: totals.totalControlDays,
      deleted: false
    };
    
    const { data: result, error } = await supabase
      .from('submissions')
      .insert(submissionData)
      .select()
      .single();

    if (error) throw error;
    return result;
  } catch (error) {
    console.error('Error saving submission:', error);
    throw error;
  }
}

export async function getSubmissions(includeDeleted: boolean = false): Promise<Submission[]> {
  try {
    let query = supabase
      .from('submissions')
      .select('*')
      .order('created_at', { ascending: false });

    if (!includeDeleted) {
      query = query.eq('deleted', false);
    }

    const { data, error } = await query;
    
    if (error) throw error;
    if (!data) return [];

    return data.map(submission => ({
      id: submission.id,
      state: submission.state,
      district: submission.district,
      croppingPattern: submission.cropping_pattern,
      herbicideApplications: submission.herbicide_applications,
      manualWeeding: submission.manual_weeding,
      herbicides: submission.herbicides,
      manualWeedingDetails: submission.manual_weeding_details,
      cropSafetyRating: submission.crop_safety_rating,
      herbicideEfficacyRating: submission.herbicide_efficacy_rating,
      total_cost: submission.total_cost,
      total_duration: submission.total_duration,
      created_at: submission.created_at,
      deleted: submission.deleted,
      deleted_at: submission.deleted_at
    }));
  } catch (error) {
    console.error('Error getting submissions:', error);
    return [];
  }
}

export async function moveToTrash(submissionId: string) {
  try {
    const { error } = await supabase
      .from('submissions')
      .update({
        deleted: true,
        deleted_at: new Date().toISOString()
      })
      .eq('id', submissionId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error moving submission to trash:', error);
    throw error;
  }
}

export async function restoreFromTrash(submissionId: string) {
  try {
    const { error } = await supabase
      .from('submissions')
      .update({
        deleted: false,
        deleted_at: null
      })
      .eq('id', submissionId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error restoring submission from trash:', error);
    throw error;
  }
}